// NumberField.tsx — labeled slider + number input combo. Emits a plain JS number
// (€ or %); unit conversion to cents/bp happens only at the API boundary (convert.ts).
import { useId, useMemo } from 'preact/hooks';
import { MONEY_STEPS, clampNiceSteps, indexToValue, valueToNearestIndex } from '../lib/niceSteps.js';

export interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  /** Optional narrower max/step for the slider thumb only — the number input still uses
   * min/max/step. Lets a control expose a wide typed range (e.g. up to 100 %) while
   * keeping the slider itself usable at a sane granularity (e.g. up to 30 %). Ignored
   * when `sliderScale`/`sliderSteps` is set (max only, to cap the notch scale — see
   * below); `sliderStep` is always ignored in that case, the scale supplies its own
   * per-segment spacing. */
  sliderMax?: number | undefined;
  sliderStep?: number | undefined;
  /** Opt-in "nice round values" slider mode (lib/niceSteps.ts): instead of a plain linear
   * range, the thumb moves over the INDEX of a precomputed array of round notches that
   * get coarser at the high end — e.g. "money" (Einmalerlag & friends): 100 € steps to
   * 2 000 €, 500 € to 10 000 €, 1 000 € to 50 000 €, 5 000 € to 200 000 €. Every notch is
   * round, 10 000 lands exactly on one, and dragging near the top can't overshoot by a
   * huge, un-hittable amount. The number input is completely unaffected — it stays
   * free-form/exact (typing 127700 works and is never rewritten); only the slider
   * thumb's rendered position snaps to the nearest notch when the current value isn't
   * one. `sliderMax` (falling back to `max`) caps which notches are exposed, so a field
   * with a lower business ceiling (e.g. "Monatliche Entnahme" at 10 000 €) doesn't offer
   * slider notches the field would never accept. */
  sliderScale?: 'money' | undefined;
  /** Explicit notch array — takes precedence over `sliderScale` when both are given.
   * Exposed mainly so a non-NumberField consumer (e.g. a compact inline slider) can reuse
   * the exact same notch set without duplicating the scale definition. */
  sliderSteps?: readonly number[] | undefined;
  unit?: string | undefined;
  hint?: string | undefined;
  disabled?: boolean | undefined;
}

const SLIDER_SCALES: Record<'money', readonly number[]> = {
  money: MONEY_STEPS,
};

export function NumberField(props: NumberFieldProps) {
  const id = useId();
  const {
    label,
    value,
    onChange,
    min,
    max,
    step,
    sliderMax,
    sliderStep,
    sliderScale,
    sliderSteps,
    unit,
    hint,
    disabled,
  } = props;
  const effectiveSliderMax = sliderMax ?? max;
  const effectiveSliderStep = sliderStep ?? step;

  const rawSteps = sliderSteps ?? (sliderScale ? SLIDER_SCALES[sliderScale] : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rawSteps is a stable module-level array or caller-supplied prop; min/effectiveSliderMax already cover the real deps.
  const niceSteps = useMemo(
    () => (rawSteps ? clampNiceSteps(rawSteps, min, effectiveSliderMax) : null),
    [rawSteps, min, effectiveSliderMax],
  );

  function handleNumber(e: Event) {
    const raw = (e.currentTarget as HTMLInputElement).value;
    if (raw === '') return;
    const n = Number(raw);
    if (Number.isFinite(n)) onChange(n);
  }

  function handleSlider(e: Event) {
    const n = Number((e.currentTarget as HTMLInputElement).value);
    if (Number.isFinite(n)) onChange(n);
  }

  function handleNiceStepSlider(e: Event) {
    if (!niceSteps) return;
    const idx = Number((e.currentTarget as HTMLInputElement).value);
    if (Number.isFinite(idx)) onChange(indexToValue(niceSteps, idx));
  }

  return (
    <div class="field">
      <label for={id}>
        {label}
        {unit ? ` (${unit})` : ''}
      </label>
      <div class="slider-row">
        {niceSteps ? (
          <input
            type="range"
            min={0}
            max={niceSteps.length - 1}
            step={1}
            value={valueToNearestIndex(niceSteps, value)}
            disabled={disabled}
            onInput={handleNiceStepSlider}
            aria-label={`${label} Schieberegler`}
          />
        ) : (
          <input
            type="range"
            min={min}
            max={effectiveSliderMax}
            step={effectiveSliderStep}
            value={clamp(value, min, effectiveSliderMax)}
            disabled={disabled}
            onInput={handleSlider}
            aria-label={`${label} Schieberegler`}
          />
        )}
        <input
          id={id}
          class="num-input"
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onInput={handleNumber}
          inputMode="decimal"
        />
      </div>
      {hint ? <div class="field-hint">{hint}</div> : null}
    </div>
  );
}

function clamp(v: number, min: number, max: number): number {
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}
